/**
 * TabsContainer - Custom element for managing accessible tabbed interfaces
 *
 * This component provides a complete tab system with:
 * - Automatic tab navigation generation from child tab blocks
 * - Radio inputs for mutual exclusivity (only one tab active per container)
 * - Full keyboard navigation (Arrow keys, Home, End, Enter, Space)
 * - ARIA attributes for screen readers and accessibility
 * - Mobile dropdown interface for responsive design
 * - Optional URL query parameter tracking for deep linking and browser history
 * - Support for dual navigation displays (desktop + mobile dropdown)
 * - Dynamic CTA slot that updates based on active tab CTA settings
 *
 * Responsibilities:
 * - Build tab navigation from tab panel data attributes
 * - Manage tab state and active tab switching
 * - Handle keyboard navigation and accessibility
 * - Maintain synchronization between desktop and mobile tab views
 * - Update URL parameters when enabled (for bookmarking/sharing)
 * - Update dynamic CTA content based on active tab
 * - Close mobile dropdown when tab is selected
 *
 * Structure:
 * - Two nav containers (default + dropdown) for dual display modes
 * - Radio inputs with matching names to keep them synchronized
 * - Labels that serve as clickable tab triggers
 * - Tab panels that contain the actual tab content
 * - Optional CTA link that updates with active tab data
 *
 * Data Attributes:
 * - data-block-id: Unique identifier for the tabs-container block
 * - data-enable-url-params: Enable URL parameter tracking (true/false)
 * - data-url-param-name: Query parameter name for URL tracking (e.g., "tab")
 *
 * Tab Panel Data Attributes:
 * - data-tab-title: Title displayed in tab triggers (required)
 * - data-tab-id: Unique identifier for the tab (used in URL params)
 * - data-tab-cta-text: Call-to-action button text (optional)
 * - data-tab-cta-url: Call-to-action button URL (optional)
 * - data-override-font-family: CSS font-family override for tab label (optional)
 *
 * Default Behavior:
 * - First tab in the container is active by default
 * - If URL parameters are enabled and a matching tab is found, that tab becomes active
 * - Mobile dropdown displays selected tab title in summary element
 *
 * @class
 * @extends {HTMLElement}
 * @customElement tabs-container
 *
 * @example
 * <tabs-container
 *   class="tabs-container--mobile-dropdown"
 *   data-block-id="abc123"
 *   data-enable-url-params="true"
 *   data-url-param-name="category"
 * >
 *   <!-- Navigation and panels populated by JavaScript -->
 * </tabs-container>
 */
if (!customElements.get('tabs-container')) {
  class TabsContainer extends HTMLElement {
    /**
     * Creates an instance of TabsContainer
     * Initializes all component properties and state
     * @constructor
     */
    constructor() {
      super();

      // State management
      /** @type {number} Currently active tab index */
      this.currentTabIndex = 0;

      // DOM element references
      /** @type {HTMLElement|null} Navigation container for default (always visible) tab nav */
      this.navDefault = null;

      /** @type {HTMLElement|null} Navigation container for dropdown tab nav (mobile) */
      this.navDropdown = null;

      /** @type {HTMLElement|null} Container for all tab panels */
      this.panelsContainer = null;

      /** @type {HTMLAnchorElement|null} Dynamic CTA link that updates per tab */
      this.ctaLink = null;

      /** @type {HTMLDetailsElement|null} Mobile dropdown details element */
      this.mobileDropdown = null;

      /** @type {HTMLSpanElement|null} Text element showing active tab in dropdown */
      this.dropdownText = null;

      // Collections
      /** @type {HTMLLabelElement[]} Array of tab trigger label elements (from default nav only) */
      this.tabTriggers = [];

      /** @type {HTMLInputElement[]} Array of radio input elements (from default nav only) */
      this.tabRadios = [];

      /** @type {HTMLElement[]} Array of tab panel elements */
      this.tabPanels = [];

      // Bound event handlers for proper cleanup
      /** @type {Function} Bound popstate event handler */
      this.boundHandlePopState = null;
    }

    /**
     * Determines if debugging is enabled for this tabs container
     * Returns true if the element has the debug class
     * @returns {boolean} Whether debugging is enabled
     */
    allowDebug() {
      return this.classList.contains('debug');
    }

    /**
     * Lifecycle callback when the element is added to the DOM
     * Initializes the component and sets up event listeners
     * @returns {void}
     */
    connectedCallback() {
      this.boundHandlePopState = this.handlePopState.bind(this);

      this.setupMobileDropdown();
      this.initTabs();

      // Re-initialize tabs when section is reloaded in design mode
      if (Shopify.designMode) {
        document.addEventListener('shopify:section:load', () => {
          this.initTabs();
        });
      }

      // Listen for browser back/forward navigation if URL params are enabled
      if (this.dataset.enableUrlParams) {
        window.addEventListener('popstate', this.boundHandlePopState);
      }
    }

    /**
     * Lifecycle callback when the element is removed from the DOM
     * Cleans up event listeners to prevent memory leaks
     * @returns {void}
     */
    disconnectedCallback() {
      if (this.dataset.enableUrlParams) {
        window.removeEventListener('popstate', this.boundHandlePopState);
      }
    }

    /**
     * Sets up references to the mobile dropdown element and its text display
     * Called during connectedCallback
     * @private
     * @returns {void}
     */
    setupMobileDropdown() {
      this.mobileDropdown = this.querySelector('.tabs-container__mobile-dropdown');
      this.dropdownText = this.querySelector('.tabs-container__mobile-dropdown-text');
    }

    /**
     * Initializes the tabs component
     * Builds tab navigation from panel data, sets up event listeners, and activates initial tab
     * Handles URL parameter parsing for deep linking
     * Called on connectedCallback and when design mode reloads
     * @private
     * @returns {void}
     */
    initTabs() {
      // Get navigation and panel containers
      this.navDefault = this.querySelector('.tabs-container__nav--default');
      this.navDropdown = this.querySelector('.tabs-container__nav--dropdown');
      this.panelsContainer = this.querySelector('.tabs-container__panels');
      this.ctaLink = this.querySelector('.tabs-container__selected-cta-link');

      // Exit early if required containers don't exist
      if ((!this.navDefault && !this.navDropdown) || !this.panelsContainer) return;

      // Get all tab panels
      this.tabPanels = Array.from(this.panelsContainer.children).filter(
        child => child.classList.contains('shopify-block--tab')
      );

      // Exit if no tab panels found
      if (this.tabPanels.length === 0) return;

      // Clear existing nav items
      if (this.navDefault) this.navDefault.innerHTML = '';
      if (this.navDropdown) this.navDropdown.innerHTML = '';

      this.tabTriggers = [];
      this.tabRadios = [];

      // Create unique radio group name for all radios in this component
      const radioGroupName = `tab-${this.dataset.blockId || Date.now()}`;

      // Build tabs from each panel
      this.tabPanels.forEach((panel, index) => {
        // Get data from panel attributes
        const tabTitle = panel.dataset.tabTitle || `Tab ${index + 1}`;
        const tabId = panel.dataset.tabId || `tab-${Date.now()}-${index}`;
        const overrideFontFamily = panel.dataset.overrideFontFamily;

        // Set panel attributes
        panel.id = tabId;
        panel.dataset.tabId = tabId;

        // Create tab navigation in both containers (default + dropdown)
        [this.navDefault, this.navDropdown].forEach((container, containerIndex) => {
          if (!container) return;

          const radioId = `radio-${tabId}-${containerIndex}`;
          const triggerId = `trigger-${tabId}-${containerIndex}`;

          // Create nav item wrapper
          const wrapper = document.createElement('div');
          wrapper.className = 'tabs-container__nav-item';

          // Create hidden radio input
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = radioGroupName; // Same name keeps both in sync
          radio.id = radioId;
          radio.className = 'tabs-container__radio';
          radio.value = index;
          radio.setAttribute('role', 'tab');
          radio.setAttribute('aria-controls', tabId);
          radio.setAttribute('aria-selected', 'false');
          // Roving tabindex; activateTab() promotes the selected tab to 0.
          // The sibling <label for> already supplies the accessible name, so no
          // aria-label here -- that would only duplicate it.
          radio.tabIndex = -1;

          // Create clickable label
          const label = document.createElement('label');
          label.className = 'tabs-container__trigger';
          label.htmlFor = radioId;
          label.id = triggerId;
          label.textContent = tabTitle;

          // Apply font-family override if provided
          if (overrideFontFamily) {
            label.style.fontFamily = overrideFontFamily;
          }

          // Handle radio change (click on tab)
          radio.addEventListener('change', () => {
            if (radio.checked) {
              this.activateTab(index);
            }
          });

          // Handle keyboard navigation
          radio.addEventListener('keydown', (e) => {
            this.handleKeyboardNavigation(e, index);
          });

          // Build DOM structure
          wrapper.appendChild(radio);
          wrapper.appendChild(label);
          container.appendChild(wrapper);

          // Track triggers and radios from default container only
          // (to avoid duplicate references)
          if (containerIndex === 0) {
            this.tabTriggers.push(label);
            this.tabRadios.push(radio);
          }
        });

        // Set panel accessibility attributes.
        //
        // aria-labelledby names the panel after its trigger in the default nav
        // (index 0). The dropdown nav builds a second trigger for the same panel,
        // but only one of the two navs is ever displayed (see the media queries in
        // blocks/tabs.liquid), and a name may reference a hidden element, so one
        // stable reference is correct here.
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `trigger-${tabId}-0`);
        panel.setAttribute('tabindex', '0');
        panel.hidden = true;
      });

      // Determine which tab to activate initially
      let initialTabIndex = 0;

      // Check URL parameters if enabled
      if (this.dataset.enableUrlParams) {
        const urlParams = new URLSearchParams(window.location.search);
        const paramName = this.dataset.urlParamName || 'tab';
        const tabValue = urlParams.get(paramName);

        if (tabValue) {
          const urlIndex = this.tabPanels.findIndex(panel => panel.dataset.tabId === tabValue);
          if (urlIndex !== -1) initialTabIndex = urlIndex;
        }
      }

      // Activate the initial tab
      this.activateTab(initialTabIndex);
    }

    /**
     * Activates a tab at the specified index
     * Updates radio states, tab panel visibility, dropdown text, and CTA content
     * Closes mobile dropdown and updates URL if applicable
     * @param {number} index - Index of the tab to activate
     * @returns {void}
     */
    activateTab(index) {
      // Validate index
      if (index < 0 || index >= this.tabPanels.length) return;

      // Update all radio inputs (both containers stay in sync due to same name)
      //
      // Radios share one group name, so the browser unchecks the previous one for
      // us. Setting `checked` on the matching radio in BOTH containers would make
      // the last one written win, which is why this assigns per radio rather than
      // once per index.
      const allRadios = this.querySelectorAll('.tabs-container__radio');
      allRadios.forEach((radio) => {
        const radioIndex = parseInt(radio.value);
        const isActive = radioIndex === index;
        radio.checked = isActive;
        radio.setAttribute('aria-selected', isActive ? 'true' : 'false');
        // Roving tabindex: only the selected tab in a tablist is a tab stop, and
        // arrow keys move between the rest (see handleKeyboardNavigation).
        radio.tabIndex = isActive ? 0 : -1;
      });

      // Update panel visibility
      this.tabPanels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });

      // Update state
      this.currentTabIndex = index;

      // Update dropdown display text
      if (this.dropdownText) {
        this.dropdownText.textContent = this.tabTriggers[index].textContent;
      }

      // Update dynamic CTA content
      this.updateCTA(index);

      // Close mobile dropdown
      this.closeMobileDropdown();

      // Update URL if enabled
      if (this.dataset.enableUrlParams) {
        this.updateURL(index);
      }
    }

    /**
     * Updates the URL with the active tab information
     * Uses replaceState to avoid adding browser history entries
     * Only called if data-enable-url-params is set
     * @private
     * @param {number} index - Index of the active tab
     * @returns {void}
     */
    updateURL(index) {
      const panel = this.tabPanels[index];
      const tabId = panel?.dataset.tabId;
      const paramName = this.dataset.urlParamName || 'tab';

      if (!tabId) return;

      const url = new URL(window.location.href);
      url.searchParams.set(paramName, tabId);

      window.history.replaceState({ tabIndex: index }, '', url.toString());
    }

    /**
     * Updates the dynamic CTA link with content from the active tab
     * Displays CTA if both text and URL are present, hides otherwise
     * @private
     * @param {number} index - Index of the active tab
     * @returns {void}
     */
    updateCTA(index) {
      if (!this.ctaLink) {
        if (this.allowDebug()) {
          console.warn('tabs-container.js: updateCTA() - CTA link element not found');
        }
        return;
      }

      const panel = this.tabPanels[index];
      const ctaText = panel?.dataset.tabCtaText;
      const ctaUrl = panel?.dataset.tabCtaUrl;

      if (this.allowDebug()) {
        console.log('tabs-container.js: updateCTA()', { index, ctaText, ctaUrl });
      }

      // Update CTA with tab data
      if (ctaText && ctaUrl) {
        this.ctaLink.textContent = ctaText;
        this.ctaLink.href = ctaUrl;
        this.ctaLink.style.display = '';
        if (this.allowDebug()) {
          console.log('tabs-container.js: updateCTA() - CTA updated:', { text: this.ctaLink.textContent, href: this.ctaLink.href });
        }
      } else {
        // Hide CTA if no data
        this.ctaLink.textContent = '';
        this.ctaLink.href = '#';
        this.ctaLink.style.display = 'none';
        if (this.allowDebug()) {
          console.log('tabs-container.js: updateCTA() - CTA hidden - no text or URL');
        }
      }
    }

    /**
     * Handles keyboard navigation within the tab list
     * Supports:
     * - Arrow Right/Down: Move to next tab
     * - Arrow Left/Up: Move to previous tab
     * - Home: Jump to first tab
     * - End: Jump to last tab
     * - Enter/Space: Activate current tab
     * @private
     * @param {KeyboardEvent} event - The keyboard event
     * @param {number} currentIndex - Index of the currently focused tab
     * @returns {void}
     */
    handleKeyboardNavigation(event, currentIndex) {
      let newIndex = currentIndex;
      let handled = false;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          newIndex = (currentIndex + 1) % this.tabTriggers.length;
          handled = true;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          newIndex = (currentIndex - 1 + this.tabTriggers.length) % this.tabTriggers.length;
          handled = true;
          break;
        case 'Home':
          newIndex = 0;
          handled = true;
          break;
        case 'End':
          newIndex = this.tabTriggers.length - 1;
          handled = true;
          break;
        case 'Enter':
        case ' ':
          this.activateTab(currentIndex);
          handled = true;
          break;
      }

      if (handled) {
        event.preventDefault();
        if (newIndex !== currentIndex) {
          this.tabRadios[newIndex].focus();
          this.activateTab(newIndex);
        }
      }
    }

    /**
     * Closes the mobile dropdown by setting open property to false
     * @private
     * @returns {void}
     */
    closeMobileDropdown() {
      if (this.mobileDropdown) {
        this.mobileDropdown.open = false;
      }
    }

    /**
     * Handles browser back/forward navigation via popstate event
     * Reads URL parameters and activates the corresponding tab
     * Only called if data-enable-url-params is set
     * @private
     * @param {PopStateEvent} event - The popstate event
     * @returns {void}
     */
    handlePopState(event) {
      if (!this.dataset.enableUrlParams) return;

      const urlParams = new URLSearchParams(window.location.search);
      const paramName = this.dataset.urlParamName || 'tab';
      const tabValue = urlParams.get(paramName);

      if (tabValue) {
        const index = this.tabPanels.findIndex(panel => panel.dataset.tabId === tabValue);
        if (index !== -1) {
          this.activateTab(index);
        }
      } else {
        // If no parameter in URL, activate first tab
        this.activateTab(0);
      }
    }
  }

  customElements.define('tabs-container', TabsContainer);
}
